import { Activity, Headset, LayoutDashboard, Ticket, Wallet, type LucideIcon } from 'lucide-react'
import { useMemo, useState, type ReactElement, type ReactNode } from 'react'

import { ModoApresentacao } from './modo-apresentacao'

import type { BlocoApresentavel } from './types'
import type { FiltroPeriodoValor } from '@/features/relatorio/components/filtro-periodo'

import { BlocoFinanceiroDenso } from '@/features/financeiro/bloco-financeiro-denso'
import { BlocoOperacaoDenso } from '@/features/operacao/bloco-operacao-denso'
import { BlocoPreVendasDenso } from '@/features/pre-vendas/bloco-pre-vendas-denso'
import { SeletorDiaApresentacao } from '@/features/pre-vendas/components/seletor-dia-apresentacao'
import { BlocoComercialDenso } from '@/features/relatorio/bloco-comercial-denso'
import { SeletorPeriodoApresentacao } from '@/features/relatorio/seletor-periodo-apresentacao'
import { BlocoSymplaDenso } from '@/features/sympla/bloco-sympla-denso'
import { hasAnyPermission } from '@/lib/permissions'
import { useAuth } from '@/providers/auth-context'

interface BlocoCatalogo {
  id: string
  titulo: string
  icone: LucideIcon
  /** Permissões necessárias (any-of). Array vazio = disponível pros logados. */
  requiresAny: readonly string[]
  render: (periodo: FiltroPeriodoValor, dia?: string) => ReactNode
  /** Controles próprios do slide (substituem o seletor de período global). */
  controles?: (dia: string | undefined, onDia: (dia?: string) => void) => ReactNode
}

// Catálogo global de blocos apresentáveis — a ordem aqui é a ordem dos slides.
// Ordem e nomes dos painéis comerciais definidos pela Lay (#f3ad):
// Pré-vendas > Vendas > Próximos Eventos.
const CATALOGO: readonly BlocoCatalogo[] = [
  {
    id: 'pre-vendas',
    titulo: 'Prospecção Inbound',
    icone: Headset,
    requiresAny: ['view:pre-vendas'],
    // O diário é por DIA, não por período: o slide troca os controles globais
    // por um seletor de dia próprio (Hoje ↔ calendário).
    render: (_periodo, dia) => <BlocoPreVendasDenso data={dia} origem="inbound" />,
    controles: (dia, onDia) => <SeletorDiaApresentacao dia={dia} onDia={onDia} />,
  },
  {
    id: 'pre-vendas-outbound',
    titulo: 'Prospecção Outbound',
    icone: Headset,
    requiresAny: ['view:pre-vendas'],
    render: (_periodo, dia) => <BlocoPreVendasDenso data={dia} origem="outbound" />,
    controles: (dia, onDia) => <SeletorDiaApresentacao dia={dia} onDia={onDia} />,
  },
  {
    id: 'comercial',
    titulo: 'Vendas',
    icone: LayoutDashboard,
    requiresAny: [],
    render: (periodo) => <BlocoComercialDenso periodo={periodo} />,
  },
  {
    id: 'sympla',
    titulo: 'Próximos Eventos',
    icone: Ticket,
    requiresAny: ['view:sympla'],
    // A agenda não tem recorte de período: os controles próprios trocam o
    // seletor global por um aviso da janela de atualização.
    render: () => <BlocoSymplaDenso />,
    controles: () => (
      <span className="text-muted-foreground text-sm">Dados ao vivo · cache de 5 min</span>
    ),
  },
  {
    id: 'financeiro',
    titulo: 'Saúde Financeira',
    icone: Wallet,
    requiresAny: ['view:financeiro'],
    render: (periodo) => <BlocoFinanceiroDenso periodo={periodo} />,
  },
  {
    id: 'operacao',
    titulo: 'Base, Operação e Cadência',
    icone: Activity,
    requiresAny: ['view:operacao'],
    render: (periodo) => <BlocoOperacaoDenso periodo={periodo} />,
  },
]

/**
 * Apresentação global: montada uma vez no shell, o botão "Apresentar" abre de
 * qualquer tela e o usuário escolhe quais blocos entram no telão. Cada bloco
 * busca os próprios dados só quando vira slide, com o período controlado aqui.
 */
export function ApresentacaoGlobal(): ReactElement {
  const { user } = useAuth()
  const perms = user?.permissions
  const [periodo, setPeriodo] = useState<FiltroPeriodoValor>({ preset: 'mes' })
  const [dia, setDia] = useState<string | undefined>(undefined)

  const blocos = useMemo<BlocoApresentavel[]>(
    () =>
      CATALOGO.filter((bloco) => hasAnyPermission(perms, bloco.requiresAny)).map((bloco) => ({
        id: bloco.id,
        titulo: bloco.titulo,
        icone: bloco.icone,
        conteudo: bloco.render(periodo, dia),
        controles: bloco.controles?.(dia, setDia),
      })),
    [perms, periodo, dia],
  )

  return (
    <ModoApresentacao
      blocos={blocos}
      controles={
        <SeletorPeriodoApresentacao
          preset={periodo.preset}
          inicio={periodo.inicio}
          fim={periodo.fim}
          onPreset={(preset) => setPeriodo({ preset })}
          onIntervalo={(inicio, fim) => setPeriodo({ inicio, fim })}
        />
      }
    />
  )
}
